<template>
    <div>
        <b-row>
            <h1 class="ml-3">{{ title }}</h1>
            <h3 class="ml-3 mt-1">({{ formatDateRangeTitle() }})</h3>
            <span class="ml-auto mr-3 mb-2 text-right">
                <i class="timestamp">Results as of {{ billingHeaderTime() }}</i>
                <div class="font-italic">
                    <actor-name
                        v-if="_timeZone"
                        :actor="$root.currentActor"
                        :use-encoded="true"
                        :text="_timeZone.replace('_', '')"
                    />
                </div>
            </span>
            <v-select
                id="filter-appointment-status"
                v-model="filter"
                :reduce="status => status.id"
                :options="statusesFilter"
                class="ml-auto mr-3"
                style="min-width: 20rem; display: none;"
                label="value"
                name="filter-appointment-status"
                multiple
                aria-placeholder="true"
                placeholder="Appointment Status (optional)"
            />
        </b-row>
        <b-card>
            <!-- <AppointmentStatusDetail :filter="filter"/> -->
            <div class="mb-4">
                <h4>
                    <span>Summary</span>
                </h4>
                <div v-if="!loaded">
                    <img src="/apps/assets/img/ajax-loader.gif" class="p-3" />
                </div>
                <div id="report-body" v-if="loaded" class="w-100">
                    <div v-if="summary.length > 0">
                        <b-table-simple bordered>
                            <b-thead head-variant="dark">
                                <tr>
                                    <th>Date</th>
                                    <th style="text-align: center;">
                                        Not Confirmed
                                    </th>
                                    <th style="text-align: center;">
                                        Confirmed
                                    </th>
                                    <th style="text-align: center;">
                                        Checked In
                                    </th>
                                    <th style="text-align: center;">In Room</th>
                                    <th style="text-align: center;">
                                        Visit Complete
                                    </th>
                                    <th style="text-align: center;">
                                        Checked Out
                                    </th>
                                    <th style="text-align: center;">
                                        Cancelled
                                    </th>
                                    <th style="text-align: center;">Total</th>
                                </tr>
                            </b-thead>

                            <tbody>
                                <tr :key="item.id" v-for="item in summary">
                                    <td>{{ item.date }}</td>
                                    <td style="text-align: center;">
                                        {{ item.not_confirmed }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.confirmed }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.checked_in }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.in_room }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.visit_complete }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.checked_out }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ item.cancelled }}
                                    </td>
                                    <td style="text-align: center;">
                                        {{ getSummaryItemTotal(item) }}
                                    </td>
                                </tr>
                            </tbody>

                            <tfoot>
                                <tr>
                                    <td>
                                        <b>Total</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.not_confirmed }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.confirmed }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.checked_in }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.in_room }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.visit_complete }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.checked_out }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.cancelled }}</b>
                                    </td>
                                    <td style="text-align: center;">
                                        <b>{{ total.all }}</b>
                                    </td>
                                </tr>
                            </tfoot>
                        </b-table-simple>
                    </div>
                    <div v-else>
                        <i>No records available</i>
                    </div>
                </div>
            </div>
            <div>
                <h4>
                    <span>Detail</span>
                </h4>
                <div v-if="!loaded">
                    <img src="/apps/assets/img/ajax-loader.gif" class="p-3" />
                </div>
                <div id="report-body" v-if="loaded" class="w-100">
                    <div v-if="filterResult.length > 0">
                        <b-table-simple bordered>
                            <b-thead head-variant="dark">
                                <tr>
                                    <th width="8%">Date / Time</th>
                                    <th width="8%">Status</th>
                                    <th width="8%">Type</th>
                                    <th width="10%">Complaint</th>
                                    <th width="8%">Assigned To</th>
                                    <th width="8%">Patient</th>
                                    <th width="8%">Patient Id</th>
                                    <th width="8%">Breed (Species)</th>
                                    <th width="10%">Client</th>
                                    <th width="8%">Client Id</th>
                                    <th width="8%">Phone</th>
                                    <th width="8%">Cancellation Reason</th>
                                </tr>
                            </b-thead>

                            <tbody>
                                <tr :key="item.id" v-for="item in filterResult">
                                    <td>{{ item.formatted_date }}</td>
                                    <td>{{ item.status }}</td>
                                    <td>{{ item.type }}</td>
                                    <td>{{ item.complaint }}</td>
                                    <td>{{ item.provider_name }}</td>
                                    <td>
                                        <a :href="getPatientURI(item)">{{
                                            item.patient
                                        }}</a>
                                    </td>
                                    <td>{{ item.patient_no }}</td>
                                    <td>{{ item.patient_breed }}</td>
                                    <td>
                                        <router-link
                                            v-if="USE_CLIENT_MODULE_VUE"
                                            :to="`/client/profile/${item.client_id}?encoded=0`"
                                        >
                                            {{ formatClientName(item) }}
                                        </router-link>
                                        <a v-else :href="getClientURI(item)">
                                            {{ formatClientName(item) }}
                                        </a>
                                    </td>
                                    <td>{{ item.client_no }}</td>
                                    <td>
                                        <span
                                            :key="phone"
                                            v-for="phone in getPhoneNumbers(
                                                item
                                            )"
                                        >
                                            {{ phone }}
                                            <br />
                                        </span>
                                    </td>
                                    <td>{{ item.cancellation_reason }}</td>
                                </tr>
                            </tbody>
                        </b-table-simple>
                        <br />
                    </div>
                    <div v-else>
                        <i>No records available</i>
                    </div>
                </div>
            </div>
        </b-card>
    </div>
</template>
<style>
li.nav-item {
    list-style-type: none;
}
.vs__dropdown-toggle {
    background-color: white;
}
</style>
<script>
import vSelect from "vue-select";
import ScheduleHelper from "@/module/schedule/helper";
import { filter, differenceWith } from "lodash";
import Report from "@/mixins/report.js";
export default {
    components: {
        vSelect,
        ActorName: () => import("@/components/commons/actor-name.vue")
    },
    mixins: [ScheduleHelper, Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment(),
                    datePickerOptions: {
                        format: this.getDateFormat()
                    }
                };
            }
        }
    },
    data() {
        return {
            loaded: false,
            locale: "en-US",
            meta: "",
            code: "",
            statuses: [],
            filter: [],
            summary: [],
            result: [],
            total: {
                not_confirmed: 0,
                confirmed: 0,
                checked_in: 0,
                in_room: 0,
                visit_complete: 0,
                checked_out: 0,
                cancelled: 0,
                all: 0
            },
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true",
            title: "Appointments By Status"
        };
    },
    computed: {
        statusesFilter() {
            return differenceWith(this.statuses, this.filter);
        },
        filterResult() {
            if (this.filter && this.filter.length > 0) {
                return filter(this.result, item =>
                    this.filter.includes(item.status_id)
                );
            } else {
                return this.result;
            }
        }
    },
    watch: {
        filter(newValue) {
            this.data.statuses = newValue;
        }
    },
    mounted() {
        let self = this;
        self.data.isShowDateRange = true;
        self.data.start = moment().tz(this._timeZone).startOf("week");
        self.data.end = moment().tz(this._timeZone).endOf("day");
        self.data.datePickerOptions.format = this.getDateFormat();

        self.init();
        self.$root.$on("report-filter-date-range:refresh", this.init);
    },
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        init(params) {
            let self = this;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
                self.data.start = params.start;
                self.data.end = params.end;
            }

            self.loaded = false;

            // --- load appointment status
            this._getStatus().then(response => {
                self.statuses = response;
            });

            // --- render summary and detail
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "/report/reports/" +
                        self.$route.params.id,
                    {
                        params: {
                            from: params ? params.from : self.data.start.unix(),
                            to: params ? params.to : self.data.end.unix()
                        }
                    }
                )
                .then(function (response) {
                    self.meta = response.data.response.resource.meta;
                    self.summary = response.data.response.resource.summary;
                    self.result = response.data.response.resource.results;
                    self.locale = self.meta.locale.replace("_", "-");
                    self.code = response.status;
                    self.title = self.capitalize(self.meta.title);
                    self.dynamicPageTitle(self.title);

                    self.renderSummary();

                    if (self.code === 200) {
                        self.loaded = true;
                    }
                });
        },
        formatDateRangeTitle() {
            if (this.data.start && this.data.end) {
                return (
                    this.data.start.format(this.getDateFormat()) +
                    " - " +
                    this.data.end.format(this.getDateFormat())
                );
            }

            return null;
        },
        formatProviderName(item) {
            return item.provider_firstname + " " + item.provider_lastname;
        },
        renderSummary() {
            let dates = [];
            let start = _.cloneDeep(this.data.start);
            let end = _.cloneDeep(this.data.end);
            end = end.add(1, "days");

            this.total = {
                not_confirmed: 0,
                confirmed: 0,
                checked_in: 0,
                in_room: 0,
                visit_complete: 0,
                checked_out: 0,
                cancelled: 0,
                all: 0
            };

            // --- construct row based on date range
            if (this.result.length > 0) {
                while (
                    start.format(this.getDateFormat()) !==
                    end.format(this.getDateFormat())
                ) {
                    let date = {
                        date: start.format(this.getDateFormat()),
                        not_confirmed: 0,
                        confirmed: 0,
                        checked_in: 0,
                        in_room: 0,
                        visit_complete: 0,
                        checked_out: 0,
                        cancelled: 0,
                        total: 0
                    };

                    for (let i = 0; i < this.summary.length; i++) {
                        let summaryObject = this.summary[i];

                        if (
                            start.format(this.getDateFormat()) ===
                            summaryObject.formatted_date
                        ) {
                            switch (summaryObject.status_id) {
                                case 1:
                                    date["not_confirmed"] = summaryObject.count;
                                    this.total.not_confirmed +=
                                        summaryObject.count;
                                    break;
                                case 2:
                                    date["confirmed"] = summaryObject.count;
                                    this.total.confirmed += summaryObject.count;
                                    break;
                                case 3:
                                    date["cancelled"] = summaryObject.count;
                                    this.total.cancelled += summaryObject.count;
                                    break;
                                case 4:
                                    date["checked_in"] = summaryObject.count;
                                    this.total.checked_in +=
                                        summaryObject.count;
                                    break;
                                case 5:
                                    date["in_room"] = summaryObject.count;
                                    this.total.in_room += summaryObject.count;
                                    break;
                                case 6:
                                    date["visit_complete"] =
                                        summaryObject.count;
                                    this.total.visit_complete +=
                                        summaryObject.count;
                                    break;
                                case 7:
                                    date["checked_out"] = summaryObject.count;
                                    this.total.checked_out +=
                                        summaryObject.count;
                                    break;
                                default:
                                    break;
                            }

                            this.total.all += summaryObject.count;
                        }
                    }

                    dates.push(date);

                    start = start.add(1, "days");
                }
            }

            this.summary = dates;
        },
        getClientURI(data) {
            if (data.client_active) {
                return (
                    process.env.CI_APPS +
                    "client/client/view/" +
                    data.client_id +
                    "/0"
                );
            }

            return false;
        },
        formatClientName(item) {
            if (item.client_active) {
                return item.client_firstname + " " + item.client_lastname;
            } else {
                return (
                    item.client_firstname +
                    " " +
                    item.client_lastname +
                    " (deleted)"
                );
            }
        },
        getPatientURI(data) {
            if (data.patient_active) {
                return (
                    process.env.CI_APPS +
                    "patient/patient/view/" +
                    data.patient_id +
                    "/0"
                );
            }

            return false;
        },
        getPhoneNumbers(item) {
            let numberArray = [];

            if (item.mobile) {
                numberArray.push(item.mobile);
            }

            if (item.work) {
                numberArray.push(item.work);
            }

            if (item.home) {
                numberArray.push(item.home);
            }

            return numberArray;
        },
        getSummaryItemTotal(item) {
            let total = 0;
            total =
                item.not_confirmed +
                item.confirmed +
                item.checked_in +
                item.in_room +
                item.visit_complete +
                item.checked_out +
                item.cancelled;

            return total;
        }
    }
};
</script>
